// Model Discovery backend API (documentation/protocol.md "Model Discovery").
import { getToken } from './api';
import type { DeviceProfileDraft, Observed } from '../usb/discovery/profile';

const BASE = '/api/admin/model-discovery/sessions';

const headers = (json = true): Record<string, string> => {
  const h: Record<string, string> = json ? { 'Content-Type': 'application/json' } : {};
  const token = getToken();
  if (token) h.Authorization = `Bearer ${token}`;
  return h;
};

export interface DiscoverySession {
  id: string;
  status: 'IN_PROGRESS' | 'COMPLETED' | 'ABANDONED';
  observed: Observed;
  profile: DeviceProfileDraft | Record<string, never>;
  device_model_id: string | null;
  created_at: string;
  updated_at: string;
  completed_at: string | null;
}

export interface ValidationIssue { code: string; message: string; key?: string }
export interface ValidationResult { ok: boolean; errors: ValidationIssue[]; warnings: ValidationIssue[] }
export interface Canonical { manufacturer_code: string; model_code: string; display_name: string }

export class DiscoveryApiError extends Error {
  constructor(public code: string, message: string, public validation?: ValidationResult) {
    super(message);
    this.name = 'DiscoveryApiError';
  }
}

async function json<T>(r: Response): Promise<T> {
  if (r.ok) return r.json() as Promise<T>;
  const text = await r.text();
  try {
    const b = JSON.parse(text) as { error?: string; validation?: ValidationResult };
    if (b.error) throw new DiscoveryApiError(b.error, b.error, b.validation);
  } catch (e) {
    if (e instanceof DiscoveryApiError) throw e;
  }
  const m = text.match(/^([A-Z_]+):\s*(.*)$/s);
  throw new DiscoveryApiError(m ? m[1] : `HTTP_${r.status}`, (m ? m[2] : text).trim() || `Request failed (${r.status})`);
}

export const listDiscoverySessions = async (): Promise<DiscoverySession[]> => json(await fetch(BASE, { headers: headers(false) }));
export const getDiscoverySession = async (id: string): Promise<DiscoverySession> => json(await fetch(`${BASE}/${id}`, { headers: headers(false) }));
export const createDiscoverySession = async (profile: DeviceProfileDraft): Promise<DiscoverySession> =>
  json(await fetch(BASE, { method: 'POST', headers: headers(), body: JSON.stringify({ profile }) }));
export const saveDiscoverySession = async (id: string, profile: DeviceProfileDraft): Promise<DiscoverySession> =>
  json(await fetch(`${BASE}/${id}`, { method: 'PUT', headers: headers(), body: JSON.stringify({ profile }) }));
export const validateDiscoverySession = async (id: string, canon?: Canonical): Promise<ValidationResult> =>
  json(await fetch(`${BASE}/${id}/validate`, { method: 'POST', headers: headers(), body: JSON.stringify(canon ?? {}) }));
export const completeDiscoverySession = async (id: string, canon: Canonical): Promise<{ device_model_id: string; lifecycle_state: string; validation: ValidationResult }> =>
  json(await fetch(`${BASE}/${id}/complete`, { method: 'POST', headers: headers(), body: JSON.stringify(canon) }));
export const abandonDiscoverySession = async (id: string): Promise<DiscoverySession> =>
  json(await fetch(`${BASE}/${id}/abandon`, { method: 'POST', headers: headers(false) }));
export const applyProfileBindings = async (id: string): Promise<{ button_actions: unknown[]; notes: string[]; validation: ValidationResult }> =>
  json(await fetch(`/api/admin/device-models/${id}/apply-profile-bindings`, { method: 'POST', headers: headers(false) }));
export const activateDeviceModel = async (id: string): Promise<{ lifecycle_state: string; validation: ValidationResult }> =>
  json(await fetch(`/api/admin/device-models/${id}/activate`, { method: 'POST', headers: headers(false) }));
